import * as constants from '../utils/constants';
import * as mainSelectors from '../main/selectors';

import { createSelector } from 'reselect';
import { getAllColorsInUse } from '../userimage/selectors';
import { getBatchSelectedTextList } from '../canvas/selectors';
import { getImprintOptionMaxColorCount } from '../imprintarea/selectors';
import { getSingleSelectedElement } from '../nextgen/main/MainPanel/selectors';
import { getStudioItemType } from '../main/selectors';
import { studioItemType as studioItemTypeEnum } from '../main/constants';

export const getElementZIndexInfo = (state) => {
  const imprintAreaKey = mainSelectors.getCurrentImprintAreaKey(state);
  let imageMaxZIndex = 0,
    imageMinZIndex = 0,
    textMaxZIndex = 0,
    textMinZIndex = 0;

  if (state.imageDict[imprintAreaKey]) {
    const allZIndex = state.imageDict[imprintAreaKey].map((e) => e.data.zIndex);
    imageMaxZIndex = allZIndex.reduce((prev, cur) => (cur > prev ? cur : prev), Number.MIN_SAFE_INTEGER);
    imageMinZIndex = allZIndex.reduce((prev, cur) => (cur < prev ? cur : prev), Number.MAX_SAFE_INTEGER);
  } else {
    imageMaxZIndex = constants.IMAGE_ELEMENT_INIT_ZINDEX;
    imageMinZIndex = constants.IMAGE_ELEMENT_INIT_ZINDEX;
  }
  if (state.textDict[imprintAreaKey]) {
    const allZIndex = state.textDict[imprintAreaKey].map((e) => e.data.zIndex);
    textMaxZIndex = allZIndex.reduce((prev, cur) => (cur > prev ? cur : prev), Number.MIN_SAFE_INTEGER);
    textMinZIndex = allZIndex.reduce((prev, cur) => (cur < prev ? cur : prev), Number.MAX_SAFE_INTEGER);
  } else {
    textMaxZIndex = constants.TEXT_ELEMENT_INIT_ZINDEX;
    textMinZIndex = constants.TEXT_ELEMENT_INIT_ZINDEX;
  }

  return {
    imageMaxZIndex: Math.max(imageMaxZIndex, constants.IMAGE_ELEMENT_INIT_ZINDEX),
    imageMinZIndex,
    textMaxZIndex: Math.max(textMaxZIndex, constants.TEXT_ELEMENT_INIT_ZINDEX),
    textMinZIndex,
    maxZIndex: Math.max(imageMaxZIndex, textMaxZIndex),
    minZIndex: Math.min(imageMinZIndex, textMinZIndex),
  };
};

export const getCurrentElement = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  (state) => state.imageDict,
  (state) => state.textDict,
  (imprintAreaKey, imageDict, textDict) => {
    let currentElement = null;

    if (imageDict[imprintAreaKey]) {
      imageDict[imprintAreaKey].forEach((element) => {
        if (element.ui.status === constants.elementStatus.SingleSelected) {
          currentElement = element;
          return false;
        }
      });
    }

    if (textDict[imprintAreaKey]) {
      textDict[imprintAreaKey].forEach((element) => {
        if (element.ui.status === constants.elementStatus.SingleSelected) {
          currentElement = element;
          return false;
        }
      });
    }
    return currentElement;
  }
);

export const getIsRotateStart = createSelector(getCurrentElement, (selectedElement) => {
  if (selectedElement) {
    return selectedElement.ui.isRotateStart;
  }
});

export const getRotateAngle = createSelector(getCurrentElement, (selectedElement) => {
  if (selectedElement) {
    return selectedElement.ui.rotateAngle || 0;
  }
});

export const getAllElementsUseColor = (color, state) => {
  const studioItemType = getStudioItemType(state);
  const currentImprintAreaKey = mainSelectors.getCurrentImprintAreaKey(state);
  if (studioItemType === studioItemTypeEnum.screenPrintedDigital) {
    const imageList = state.imageDict[currentImprintAreaKey];

    let elements = [];
    imageList &&
      imageList.forEach((userimg) => {
        const hasColor =
          userimg.data &&
          userimg.data.colors.some((c) => c.colorId === color.colorId && c.colorType === color.colorType);
        if (hasColor) {
          elements.push(userimg);
        }
      });

    const textList = state.textDict[currentImprintAreaKey];
    textList &&
      textList.forEach((text) => {
        if (text.data.color.colorId === color.colorId && text.data.color.colorType === color.colorType) {
          elements.push(text);
        }
      });

    return elements;
  }
};

export const getColorChangeElements = (color, newColor, state) => {
  const maxColorCount = getImprintOptionMaxColorCount(state);
  const batchSelectedText = getBatchSelectedTextList(state);
  const batchMode = batchSelectedText && batchSelectedText.length > 1;
  const selectedElement = getSingleSelectedElement(state);
  const selectedElementGuids = batchMode ? batchSelectedText.map((text) => text.ui.guid) : [selectedElement.ui.guid];
  const colorsInUse = getAllColorsInUse(state);
  const newColorIsInUse = newColor && colorsInUse.some((c) => c.colorId === newColor.colorId);
  const elementsUseColor = getAllElementsUseColor(color, state);

  const changingElements = elementsUseColor.filter((x) => selectedElementGuids.indexOf(x.ui.guid) > -1);
  let isChangingText = changingElements.some((x) => x.data.imprintText || x.data.defaultText);
  let elementsNeedChangeColor = elementsUseColor;
  if ((maxColorCount > 1 && colorsInUse.length < maxColorCount) || newColorIsInUse) {
    if (isChangingText) {
      elementsNeedChangeColor = elementsNeedChangeColor.filter((x) => x.data.imprintText || x.data.defaultText);
    } else {
      elementsNeedChangeColor = changingElements;
    }
  }

  const changeAll = maxColorCount === 1 || (colorsInUse.length >= maxColorCount && !newColorIsInUse);

  return { elements: elementsNeedChangeColor, changeAll };
};
